"use client";
import React, { useState } from 'react';
import { X, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useEditMode } from '../EditModeProvider';
import EditableText from '../EditableText';
import { useSmartCaptcha } from '../../lib/useSmartCaptcha';
import { isCompletePhone, toE164 } from '../../lib/phoneMask';
import PhoneInput from './PhoneInput';

const LeadModal = ({ isOpen, onClose, buttonColor }) => {
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { getContent } = useEditMode();
  const { getCaptchaToken } = useSmartCaptcha();

  const btnColor = buttonColor || getContent('lead_modal_submit_btn_color', '#17c6a6');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) {
      setError('Необходимо согласие с условиями');
      return;
    }
    if (!formData.name || !formData.phone) {
      setError('Пожалуйста, заполните все поля');
      return;
    }
    if (!isCompletePhone(formData.phone)) {
      setError('Введите телефон полностью');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const smartToken = await getCaptchaToken();
      await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/leads`, { ...formData, phone: toE164(formData.phone), smartToken });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', phone: '' });
        setAgreed(false);
        onClose();
      }, 3000);
    } catch (err) {
      setError('Произошла ошибка при отправке заявки. Попробуйте позже.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#1E293B] rounded-[24px] w-full max-w-md relative overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors z-50"
        >
          <X className="w-6 h-6" />
        </button>

        <div className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 pr-8">
            <EditableText contentKey="lead_modal_title" defaultValue="Оставьте заявку на расчёт стоимости" />
          </h2>

          {success ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  <EditableText contentKey="lead_modal_success_title" defaultValue="Спасибо за заявку!" />
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                  <EditableText contentKey="lead_modal_success_desc" defaultValue="Мы свяжемся с вами в ближайшее время." />
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  placeholder="Имя"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-4 bg-gray-100 dark:bg-zinc-800 border-transparent focus:border-transparent focus:ring-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 outline-none transition-all"
                />
              </div>
              <div>
                <PhoneInput
                  value={formData.phone}
                  onChange={(phone) => setFormData({ ...formData, phone })}
                  className="w-full px-4 py-4 bg-gray-100 dark:bg-zinc-800 border-transparent focus:border-transparent focus:ring-0 rounded-xl text-gray-900 dark:text-white placeholder-gray-500 outline-none transition-all"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{ background: btnColor }}
                className="w-full py-4 px-6 text-white font-semibold rounded-xl flex justify-center items-center hover:brightness-95 active:brightness-90 transition-all"
              >
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : <EditableText contentKey="lead_modal_submit_btn" defaultValue="Получить расчёт стоимости" />}
              </button>

              <label className="flex items-start space-x-3 mt-4 cursor-pointer group">
                <div className="flex-shrink-0 mt-1">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB] cursor-pointer"
                  />
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                  Я согласен с условиями{' '}
                  <a href="https://ses-rb.ru/polzovatelskoe-soglashenie/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700 dark:hover:text-gray-200">пользовательского соглашения</a>,{' '}
                  <a href="https://ses-rb.ru/politika-konfidentsialnosti/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700 dark:hover:text-gray-200">политики конфиденциальности</a> и{' '}
                  <a href="https://ses-rb.ru/soglasiye-na-obrabotku-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-gray-700 dark:hover:text-gray-200">согласия на обработку персональных данных</a>
                </span>
              </label>
            </form>
          )}
        </div>

        {/* Footer Indicator */}
        {!success && (
          <div className="bg-gray-50 dark:bg-zinc-800/50 px-8 py-4 flex items-center justify-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-sm text-gray-600 dark:text-gray-300">
              <EditableText contentKey="lead_modal_footer_text" defaultValue="Среднее время ответа — 7 минут" />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadModal;